import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { PageHeader, Button, Card, StatCard, Modal, FormField, Input, Select, statusBadge, formatCurrency, formatDate, Badge } from '../ui';
import { Plus, Download, Send, CreditCard, DollarSign, AlertTriangle } from 'lucide-react';

export default function Payments() {
  const { payments, teams, tournaments, addPayment, updatePayment } = useApp();
  const { isAdmin } = useAuth();
  const [filter, setFilter] = useState('all');
  const [showRecord, setShowRecord] = useState(false);
  const [showStripe, setShowStripe] = useState(null);
  const [recordForm, setRecordForm] = useState({ teamId: '', tournamentId: '', amount: '', method: 'manual', notes: '', date: new Date().toISOString().split('T')[0] });
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeDone, setStripeDone] = useState(false);

  const getTeam = id => teams.find(t => t.id === id);
  const getTournament = id => tournaments.find(t => t.id === id);

  const totals = {
    paid: payments.filter(p => p.status === 'paid').reduce((s, p) => s + p.amount, 0),
    pending: payments.filter(p => p.status === 'pending').reduce((s, p) => s + p.amount, 0),
    overdue: payments.filter(p => p.status === 'overdue').reduce((s, p) => s + p.amount, 0),
    partial: payments.filter(p => p.status === 'partial').reduce((s, p) => s + (p.amount - (p.paidAmount || 0)), 0),
  };

  const filtered = filter === 'all' ? payments : payments.filter(p => p.status === filter);

  const submitRecord = (e) => {
    e.preventDefault();
    addPayment({ ...recordForm, amount: Number(recordForm.amount), status: 'paid', invoiceNum: 'INV-' + String(payments.length + 1).padStart(3, '0') });
    setShowRecord(false);
    setRecordForm({ teamId: '', tournamentId: '', amount: '', method: 'manual', notes: '', date: new Date().toISOString().split('T')[0] });
  };

  const fakeStripeCheckout = () => {
    setStripeLoading(true);
    setTimeout(() => { setStripeLoading(false); setStripeDone(true); }, 2000);
  };

  return (
    <div>
      <PageHeader title="Payments" subtitle="Track registrations fees, send invoices, and record payments"
        actions={isAdmin && <>
          <Button variant="secondary" size="sm"><Download size={14} /> Export</Button>
          <Button variant="primary" onClick={() => setShowRecord(true)}><Plus size={15} /> Record payment</Button>
        </>}
      />

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: '1.5rem' }}>
        <StatCard label="Collected" value={formatCurrency(totals.paid)} sub={`${payments.filter(p=>p.status==='paid').length} payments`} icon={<DollarSign size={18}/>} />
        <StatCard label="Pending" value={formatCurrency(totals.pending)} sub={`${payments.filter(p=>p.status==='pending').length} teams`} subColor="#f57f17" icon={<CreditCard size={18}/>} />
        <StatCard label="Overdue" value={formatCurrency(totals.overdue)} sub={`${payments.filter(p=>p.status==='overdue').length} teams`} subColor="#c62828" icon={<AlertTriangle size={18}/>} />
        <StatCard label="Partial balance" value={formatCurrency(totals.partial)} sub="Remaining due" subColor="#8e24aa" icon={<DollarSign size={18}/>} />
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: '1.25rem', background: 'var(--surface-2)', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[['all','All'], ['paid','Paid'], ['pending','Pending'], ['overdue','Overdue'], ['partial','Partial']].map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)}
            style={{ padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600, background: filter === val ? 'var(--surface-0)' : 'transparent', color: filter === val ? 'var(--text-primary)' : 'var(--text-muted)', boxShadow: filter === val ? 'var(--shadow-sm)' : 'none', transition: 'all 0.15s' }}>
            {label}
          </button>
        ))}
      </div>

      <Card padding="0">
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--surface-1)' }}>
              {['Invoice','Team','Event','Amount','Method','Date','Status','Actions'].map(h => (
                <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(pay => {
              const team = getTeam(pay.teamId);
              const tourn = getTournament(pay.tournamentId);
              return (
                <tr key={pay.id} style={{ borderBottom: '1px solid var(--border)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-1)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '11px 14px', fontWeight: 700, fontSize: 12, color: 'var(--text-muted)' }}>{pay.invoiceNum}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: team?.color || '#94a3b8' }} />
                      <span style={{ fontWeight: 600 }}>{team?.name || '—'}</span>
                    </div>
                  </td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-secondary)', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tourn?.name}</td>
                  <td style={{ padding: '11px 14px', fontWeight: 700 }}>
                    {formatCurrency(pay.amount)}
                    {pay.status === 'partial' && <div style={{ fontSize: 11, color: '#8e24aa' }}>{formatCurrency(pay.paidAmount || 0)} paid</div>}
                  </td>
                  <td style={{ padding: '11px 14px' }}>{pay.method ? statusBadge(pay.method) : <span style={{ color: 'var(--text-muted)', fontSize: 13 }}>—</span>}</td>
                  <td style={{ padding: '11px 14px', color: 'var(--text-secondary)', fontSize: 13 }}>{pay.date ? formatDate(pay.date) : '—'}</td>
                  <td style={{ padding: '11px 14px' }}>{statusBadge(pay.status)}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {pay.status === 'paid' && <button style={{ fontSize: 12, color: 'var(--gold-main)', background: 'none', border: 'none', cursor: 'pointer' }}>Receipt</button>}
                      {['pending','overdue'].includes(pay.status) && isAdmin && <>
                        <button onClick={() => setShowStripe(pay)} style={{ fontSize: 12, color: '#fff', background: '#6c63ff', border: 'none', padding: '3px 8px', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Pay via Stripe</button>
                        <button style={{ fontSize: 12, color: '#f57f17', background: 'none', border: 'none', cursor: 'pointer' }}>
                          <Send size={11} style={{ marginRight: 3 }} />Remind
                        </button>
                      </>}
                      {pay.status === 'partial' && isAdmin && <button onClick={() => updatePayment(pay.id, { status: 'paid', paidAmount: pay.amount, date: new Date().toISOString().split('T')[0] })} style={{ fontSize: 12, color: '#6eca7c', background: 'rgba(40,120,60,0.2)', border: 'none', padding: '3px 8px', borderRadius: 6, cursor: 'pointer', fontWeight: 600 }}>Mark paid</button>}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Record Payment Modal */}
      <Modal open={showRecord} onClose={() => setShowRecord(false)} title="Record manual payment">
        <form onSubmit={submitRecord} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <FormField label="Team" required>
              <Select value={recordForm.teamId} onChange={e => setRecordForm(p => ({...p, teamId: e.target.value}))}>
                <option value="">Select…</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </FormField>
            <FormField label="Event" required>
              <Select value={recordForm.tournamentId} onChange={e => setRecordForm(p => ({...p, tournamentId: e.target.value}))}>
                <option value="">Select…</option>
                {tournaments.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </Select>
            </FormField>
            <FormField label="Amount ($)" required>
              <Input type="number" value={recordForm.amount} onChange={e => setRecordForm(p => ({...p, amount: e.target.value}))} placeholder="350" min="0" required />
            </FormField>
            <FormField label="Payment method">
              <Select value={recordForm.method} onChange={e => setRecordForm(p => ({...p, method: e.target.value}))}>
                <option value="manual">Manual (cash/check)</option>
                <option value="stripe">Stripe</option>
                <option value="venmo">Venmo</option>
                <option value="zelle">Zelle</option>
              </Select>
            </FormField>
            <FormField label="Payment date">
              <Input type="date" value={recordForm.date} onChange={e => setRecordForm(p => ({...p, date: e.target.value}))} />
            </FormField>
            <FormField label="Notes">
              <Input value={recordForm.notes} onChange={e => setRecordForm(p => ({...p, notes: e.target.value}))} placeholder="Check #, memo, etc." />
            </FormField>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
            <Button type="button" variant="secondary" onClick={() => setShowRecord(false)}>Cancel</Button>
            <Button type="submit" variant="primary">Record payment</Button>
          </div>
        </form>
      </Modal>

      {/* Stripe Modal */}
      <Modal open={!!showStripe} onClose={() => { setShowStripe(null); setStripeDone(false); setStripeLoading(false); }} title="Stripe payment" width={440}>
        {!stripeDone ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
              <div style={{ fontSize: 13, color: '#6c63ff', fontWeight: 700, marginBottom: 4 }}>Stripe Checkout</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>{formatCurrency(showStripe?.amount)}</div>
              <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 2 }}>{getTeam(showStripe?.teamId)?.name} · {getTournament(showStripe?.tournamentId)?.name}</div>
            </div>
            <FormField label="Card number">
              <Input value="4242 4242 4242 4242" readOnly style={{ background: 'var(--surface-1)' }} />
            </FormField>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label="Expiry"><Input value="12/27" readOnly style={{ background: 'var(--surface-1)' }} /></FormField>
              <FormField label="CVC"><Input value="123" readOnly style={{ background: 'var(--surface-1)' }} /></FormField>
            </div>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', background: 'var(--surface-1)', padding: '8px 12px', borderRadius: 8 }}>
              🔒 Payments are processed securely via Stripe. In production, this integrates with your Stripe account using Payment Intents.
            </p>
            <Button variant="primary" style={{ width: '100%', justifyContent: 'center', background: '#6c63ff' }} onClick={fakeStripeCheckout} disabled={stripeLoading}>
              {stripeLoading ? 'Processing…' : `Pay ${formatCurrency(showStripe?.amount)}`}
            </Button>
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '1.5rem 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✓</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--gold-bright)', marginBottom: 6 }}>Payment successful!</h3>
            <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
              {formatCurrency(showStripe?.amount)} received from {getTeam(showStripe?.teamId)?.name}
            </p>
            <Button variant="primary" style={{ marginTop: '1.25rem' }} onClick={() => {
              updatePayment(showStripe.id, { status: 'paid', method: 'stripe', date: new Date().toISOString().split('T')[0] });
              setShowStripe(null); setStripeDone(false);
            }}>Done</Button>
          </div>
        )}
      </Modal>
    </div>
  );
}
